import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios';
import { ApiError } from '../types';
import { storage } from '../../utils/storage';

// Resolve API base URL with Vite and Create React App style fallbacks,
// then fallback to the provided ECR URL.
const DEFAULT_API_URL =
  'https://593793029190.dkr.ecr.eu-north-1.amazonaws.com/autoplace-backend-api';

// Vite exposes env vars on import.meta.env. Use VITE_API_BASE_URL first,
// then fallback to DEFAULT_API_URL.
export const API_BASE_URL: string =
  import.meta.env.VITE_API_BASE_URL || DEFAULT_API_URL;

export const axiosInstance: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: false, // Set to true if backend requires credentials
});

// Log the API base URL on initialization for debugging
console.log('API Base URL:', API_BASE_URL);

axiosInstance.interceptors.request.use(
  config => {
    const token = storage.getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  error => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError) => {
    // Log detailed error information for debugging
    console.error('API Error Details:', {
      message: error.message,
      code: error.code,
      request: error.request,
      response: error.response,
      config: {
        url: error.config?.url,
        baseURL: error.config?.baseURL,
        method: error.config?.method,
        headers: error.config?.headers,
      },
    });

    if (error.response) {
      // Server responded with error status
      return Promise.reject(error.response.data as ApiError);
    }
    if (error.request) {
      // Request was made but no response received
      // This often indicates CORS or network issues
      const errorMessage =
        error.code === 'ERR_NETWORK' || error.message.includes('Network Error')
          ? 'Network error: Unable to reach server. Check if backend is running and CORS is configured.'
          : 'No response from server.';

      console.error('Network Error - Possible causes:');
      console.error('1. Backend server not running on', API_BASE_URL);
      console.error('2. CORS not configured on backend');
      console.error('3. Network connectivity issue');

      return Promise.reject({
        success: false,
        message: errorMessage,
      });
    }
    return Promise.reject({
      success: false,
      message: error.message || 'An unexpected error occurred.',
    });
  }
);
